import { useState, useEffect } from "react";
import { Select, Box, Typography } from "@bca/ui-library";
import { formatCount } from "./formatCount";
import { MappedRangeFacetItem } from "./filters.type";
import { filterConstants } from "./filtersConfig";
import formatUnit from "./formatUnit";

const sumReducer = (previousValue, currentValue) =>
  previousValue + currentValue;
const removeUnit = (value: string): string =>
  value?.replace("MONTH", "")?.replace("YEAR", "");

const getMinOptionsList = (
  rangeSubfilters: MappedRangeFacetItem[],
  selectedMax: string,
  categoryKey: string
) => {
  let maxValue = selectedMax;
  const isMileageMaxLimit =
    categoryKey === "Mileage" && maxValue === filterConstants.maxMileageLimit;
  const isCAPCleanMaxLimit =
    categoryKey === "CapCleanPrice" &&
    maxValue === filterConstants.maxCAPCleanLimit;
  const isVehicleAgeMaxLimit =
    categoryKey === "DateRegistered" &&
    (maxValue === filterConstants.maxVehicleAgeLimit || maxValue === "0YEAR");

  if (isMileageMaxLimit || isCAPCleanMaxLimit) {
    maxValue = "0";
  }

  if (isVehicleAgeMaxLimit) {
    maxValue = "0YEAR";
  }

  const indexOfSelectedMaxValue = rangeSubfilters.findIndex(
    (item) => `${item.to}${item.toUnit}` === maxValue
  );

  const listToDisplay = rangeSubfilters.filter(
    (item, index) => index <= indexOfSelectedMaxValue
  );

  return [...listToDisplay];
};

const getMaxOptionsList = (rangeSubfilters, selectedMin) => {
  const indexOfSelectedMinValue = rangeSubfilters.findIndex(
    (item) => item.from.toString() === removeUnit(selectedMin)
  );

  const listToDisplay = rangeSubfilters.filter(
    (item, index) => index >= indexOfSelectedMinValue
  );

  return [...listToDisplay];
};

const getCountForMaxOption = (
  rangeSubfilters: MappedRangeFacetItem[],
  selectedMin: string,
  maxValueItem: MappedRangeFacetItem
) => {
  const indexOfSelectedMaxValue = rangeSubfilters.findIndex(
    (item) =>
      `${item.to}${item.toUnit}` === `${maxValueItem.to}${maxValueItem.toUnit}`
  );

  const indexOfSelectedMinValue = rangeSubfilters.findIndex(
    (item) => `${item.from}${item.fromUnit}` === selectedMin
  );

  if (indexOfSelectedMaxValue < indexOfSelectedMinValue) {
    return "-1";
  }

  const itemsToAdd = rangeSubfilters.filter(
    (item, index) =>
      index >= indexOfSelectedMinValue && index <= indexOfSelectedMaxValue
  );

  const counts = itemsToAdd.map((item) => item.count);

  const sum = counts.reduce(sumReducer);
  return formatCount(sum);
};

const getCountForMinOption = (
  rangeSubfilters: MappedRangeFacetItem[],
  selectedMax: string,
  minValueItem: MappedRangeFacetItem
) => {
  let maxValue = selectedMax;
  if (selectedMax === "99YEAR") {
    maxValue = "0YEAR";
  }

  const indexOfSelectedMaxValue = rangeSubfilters.findIndex(
    (item) => `${item.to}${item.toUnit}` === maxValue
  );

  const indexOfSelectedMinValue = rangeSubfilters.findIndex(
    (item) =>
      `${item.from}${item.fromUnit}` ===
      `${minValueItem.from}${minValueItem.fromUnit}`
  );

  if (indexOfSelectedMaxValue < indexOfSelectedMinValue) {
    return "-1";
  }

  const itemsToAdd = rangeSubfilters.filter(
    (item, index) =>
      index >= indexOfSelectedMinValue && index <= indexOfSelectedMaxValue
  );

  const counts = itemsToAdd.map((item) => item.count);

  const sum = counts.reduce(sumReducer);
  return formatCount(sum);
};

const CustomLabel = ({ text }: { text: string }) => (
  <Typography variant="body-small" color="footer-blue" fontWeight="semiBold">
    {text}
  </Typography>
);

const getMinOptionValue = (
  categoryKey,
  rangeSubfilters: MappedRangeFacetItem[],
  maxValue,
  item: MappedRangeFacetItem
) => {
  let referenceMaxValue = maxValue;
  if (maxValue === filterConstants.maxMileageLimit) {
    referenceMaxValue = "0";
  }
  const countValue = getCountForMinOption(
    rangeSubfilters,
    referenceMaxValue,
    item
  );
  const value = formatCount(item.from);

  if (categoryKey === "CapCleanPrice") {
    return `£${value} (${countValue})`;
  }

  if (categoryKey === "Mileage") {
    return `${value} miles (${countValue})`;
  }

  if (categoryKey === "DateRegistered") {
    const unit = formatUnit(item.fromUnit);
    return `${formatCount(item.from)} ${unit} (${countValue})`;
  }
};

const getMaxOptionValue = (
  categoryKey,
  rangeSubfilters: MappedRangeFacetItem[],
  minValue,
  item: MappedRangeFacetItem
) => {
  const countValue = getCountForMaxOption(rangeSubfilters, minValue, item);

  if (item.to === 0) {
    const lastMinValueFromAPI =
      rangeSubfilters[rangeSubfilters?.length - 1]?.from;
    const formattedDisplayValue = formatCount(lastMinValueFromAPI);

    if (categoryKey === "CapCleanPrice") {
      return `£${formattedDisplayValue}+ (${countValue})`;
    }

    if (categoryKey === "Mileage") {
      return `${formattedDisplayValue}+ miles (${countValue})`;
    }

    if (categoryKey === "DateRegistered") {
      const unit = formatUnit(item.toUnit);
      return `${formattedDisplayValue}+ ${unit} (${countValue})`;
    }
  }

  const value = formatCount(item.to);
  if (categoryKey === "CapCleanPrice") {
    return `£${value} (${countValue})`;
  }

  if (categoryKey === "Mileage") {
    return `${value} miles (${countValue})`;
  }

  if (categoryKey === "DateRegistered") {
    const unit = formatUnit(item.toUnit);
    return `${value} ${unit} (${countValue})`;
  }
};

const getMaxForDateRegistered = (item: MappedRangeFacetItem): string =>
  `${item.to}${item.toUnit}`;

const getMInForDateRegistered = (item: MappedRangeFacetItem): string =>
  `${item.from}${item.fromUnit}`;

const SelectGroup = ({
  rangeSubfilters,
  categoryKey,
  onChange,
  searchResultsLoading,
  appliedCheckboxes,
  labels,
}: {
  rangeSubfilters: MappedRangeFacetItem[];
  categoryKey: string;
  onChange: any;
  searchResultsLoading: boolean;
  appliedCheckboxes: any;
  labels: string[];
}) => {
  const firstItem = rangeSubfilters[0];
  const defaultMinValue =
    categoryKey === "DateRegistered"
      ? getMInForDateRegistered(firstItem)
      : rangeSubfilters[0].from.toString();
  const lastItem = rangeSubfilters[rangeSubfilters?.length - 1];

  const defaultMaxValue =
    categoryKey === "DateRegistered"
      ? getMaxForDateRegistered(lastItem)
      : lastItem.to.toString();
  const [minValue, setMinValue] = useState<string>(defaultMinValue);
  const [maxValue, setMaxValue] = useState<string>(defaultMaxValue);

  const [minOptions, setMinOptions] = useState(rangeSubfilters);
  const [maxOptions, setMaxOptions] = useState(rangeSubfilters);

  // scenario: user navigates to Search page with range filter in URL
  useEffect(() => {
    const selectedRangeValues = appliedCheckboxes.find(
      (item) => item.categoryKey === categoryKey
    );

    if (selectedRangeValues) {
      const rangeValues = selectedRangeValues.filterKey?.split("..");
      const newDefaultMinValue = rangeValues[0];
      setMinValue(newDefaultMinValue);

      let newDefaultMaxValue = rangeValues[1];
      if (
        newDefaultMaxValue === filterConstants.maxMileageLimit ||
        newDefaultMaxValue === filterConstants.maxCAPCleanLimit
      ) {
        newDefaultMaxValue = "0";
      }

      if (newDefaultMaxValue === filterConstants.maxVehicleAgeLimit) {
        newDefaultMaxValue = "0YEAR";
      }
      setMaxValue(newDefaultMaxValue);
    }
  }, []);

  useEffect(() => {
    const newMaxOptions = getMaxOptionsList(rangeSubfilters, minValue);
    setMaxOptions([...newMaxOptions]);

    const newMinOptions = getMinOptionsList(
      rangeSubfilters,
      maxValue,
      categoryKey
    );
    setMinOptions([...newMinOptions]);
  }, [minValue, maxValue, rangeSubfilters, categoryKey]);

  const handleMinValueChange = (event) => {
    const newMinValue = event.target.value as string;
    setMinValue(newMinValue);

    if (categoryKey === "Mileage" && maxValue === "0") {
      const filterKey = `${newMinValue}..${filterConstants.maxMileageLimit}`;
      onChange(filterKey, categoryKey);
      return;
    }

    if (categoryKey === "CapCleanPrice" && maxValue === "0") {
      const filterKey = `${newMinValue}..${filterConstants.maxCAPCleanLimit}`;
      onChange(filterKey, categoryKey);
      return;
    }

    if (categoryKey === "DateRegistered" && maxValue === "0YEAR") {
      const filterKey = `${newMinValue}..${filterConstants.maxVehicleAgeLimit}`;
      onChange(filterKey, categoryKey);
      return;
    }

    const filterKey = `${newMinValue}..${maxValue}`;
    onChange(filterKey, categoryKey);
  };

  const handleMaxValueChange = (event) => {
    const newMaxValue = event.target.value;

    if (categoryKey === "Mileage" && newMaxValue === defaultMaxValue) {
      setMaxValue(defaultMaxValue);
      const filterKey = `${minValue}..${filterConstants.maxMileageLimit}`;
      onChange(filterKey, categoryKey);
      return;
    }

    if (categoryKey === "CapCleanPrice" && newMaxValue === defaultMaxValue) {
      setMaxValue(defaultMaxValue);
      const filterKey = `${minValue}..${filterConstants.maxCAPCleanLimit}`;
      onChange(filterKey, categoryKey);
      return;
    }

    if (categoryKey === "DateRegistered" && newMaxValue === defaultMaxValue) {
      setMaxValue(defaultMaxValue);
      const filterKey = `${minValue}..${filterConstants.maxVehicleAgeLimit}`;
      onChange(filterKey, categoryKey);
      return;
    }

    setMaxValue(newMaxValue);
    const filterKey = `${minValue}..${newMaxValue}`;
    onChange(filterKey, categoryKey);
  };

  return (
    <Box>
      <Box display="flex" flexDirection="column">
        <CustomLabel text={labels[0]} />
        <Select
          disabled={searchResultsLoading}
          id={`${categoryKey}-min`}
          aria-label="from"
          value={minValue}
          onChange={handleMinValueChange}
          width="100%"
        >
          {rangeSubfilters &&
            minOptions.map((item, index) =>
              getCountForMinOption(rangeSubfilters, maxValue, item) !== "0" ? (
                <option
                  value={
                    categoryKey === "DateRegistered"
                      ? `${item.from}${item.fromUnit}`
                      : item.from
                  }
                  key={index}
                >
                  {getMinOptionValue(categoryKey, minOptions, maxValue, item)}
                </option>
              ) : null
            )}
        </Select>
      </Box>
      <Box display="flex" flexDirection="column">
        <CustomLabel text={labels[1]} />
        <Select
          disabled={searchResultsLoading}
          id={`${categoryKey}-max`}
          aria-label="to"
          value={maxValue}
          onChange={handleMaxValueChange}
          width="100%"
        >
          {rangeSubfilters &&
            maxOptions.map((item, index) =>
              getCountForMaxOption(rangeSubfilters, minValue, item) !== "0" ? (
                <option
                  value={
                    categoryKey === "DateRegistered"
                      ? `${item.to}${item.toUnit}`
                      : item.to
                  }
                  key={index}
                >
                  {getMaxOptionValue(categoryKey, maxOptions, minValue, item)}
                </option>
              ) : null
            )}
        </Select>
      </Box>
    </Box>
  );
};

export default SelectGroup;
